<template>
  <v-card class="comments-card modern" elevation="0">
    <!-- Header -->
    <v-card-title class="py-3 px-4 d-flex align-center justify-space-between header">
      <div class="d-flex align-center">
        <span class="text-h6 font-weight-600">{{ $t('Comments') }}</span>

        <v-chip
          v-if="comments.length"
          class="ml-3 count-chip"
          label
          small
          color="primary"
          :text-color="textOn($vuetify.theme.currentTheme.primary)"
          :ripple="false"
        >
          <v-icon left small>mdi-message-reply-text-outline</v-icon>{{ comments.length }}
        </v-chip>
      </div>

     
    </v-card-title>

    <!-- Create -->
    <v-card-text class="px-4 pt-4 pb-2">
      <div class="cm-row">
        <div class="cm-field">
          <textarea
            ref="newCommentEl"
            v-model="newComment"
            class="cm-textarea"
            rows="1"
            maxlength="2000"
            :placeholder="$t('Write a comment...')"
            :disabled="posting || loading"
            @input="fitTextarea($event.target)"
            @keydown.ctrl.enter.prevent="createComment"
          ></textarea>
          <span v-if="(newComment || '').length > 1800" class="cm-count">{{ newComment.length }}/2000</span>
        </div>
        <v-btn
          color="primary"
          class="elevate-on-hover post-btn font-made"
          depressed
          :loading="posting"
          :disabled="!newCommentTrimmed || posting || loading"
          @click="createComment"
        >
          {{ $t('Post') }}<svg class="post-icon" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="m22.1012 10.5616-19.34831-9.43824c-.1664-.08117-.34912-.12336-.53427-.12336-.67302 0-1.21862.5456-1.21862 1.21862v.03517c0 .16352.02005.32643.05971.48507l1.85597 7.42384c.05069.2028.22214.3526.42986.3757l8.15756.9064c.2829.0314.4969.2705.4969.5552s-.214.5238-.4969.5552l-8.15756.9064c-.20772.0231-.37917.1729-.42986.3757l-1.85597 7.4238c-.03966.1587-.05971.3216-.05971.4851v.0352c0 .673.5456 1.2186 1.21862 1.2186.18515 0 .36787-.0422.53427-.1234l19.34831-9.4382c.5499-.2682.8988-.8265.8988-1.4384s-.3489-1.1702-.8988-1.4384z"/></svg>
        </v-btn>
      </div>
    </v-card-text>

    <!-- List -->
    <v-card-text class="px-0 pt-0">
      <v-skeleton-loader v-if="loading" type="list-item@3" class="px-4" />

      <template v-else>
        <div v-if="comments.length === 0" class="grey--text text-center my-6 empty-state">
          <div>{{ $t('No comments yet.') }}</div>
        </div>

        <transition-group name="fade-move" tag="div">
          <v-list
            v-if="comments.length"
            key="list"
            dense
            class="py-0"
          >
            <v-list-item
              v-for="c in comments"
              :key="c.id || c.__tempId"
              class="rounded-lg mb-2 comment-item mx-3"
            >
              <v-list-item-avatar>
                <v-avatar size="36" class="elev-1 avatar-grad">
                  <span class="avatar-initials">
                    {{ initials(c.firstName, c.lastName) }}
                  </span>
                </v-avatar>
              </v-list-item-avatar>

              <v-list-item-content>
                <div class="d-flex align-center mb-1">
                  <div class="font-weight-600 mr-2 name">
                    {{ c.firstName }} {{ c.lastName }}
                  </div>
                  <v-chip v-if="isCurrentUser(c)" x-small label class="ml-0 you-chip" color="grey lighten-3" :text-color="darkText">
                    <v-icon left x-small>mdi-account-check-outline</v-icon>{{ $t('You') }}
                  </v-chip>
                  <div class="grey--text caption ml-3">
                    <span v-if="c.createdAt">
                      <v-icon x-small class="mr-1">mdi-calendar-clock-outline</v-icon>{{ formatDate(c.createdAt) }}
                    </span>
                    <v-icon
                      v-if="c.__tempId"
                      small
                      class="ml-2 pulse"
                      color="grey"
                      :title="$t('Sending…')"
                    >mdi-cloud-upload-outline</v-icon>
                  </div>
                </div>

                <!-- Read / Edit -->
                <div v-if="editingId !== c.id" class="comment-body grey--text text--darken-2">
                  {{ c.comment }}
                </div>

                <div v-else class="mt-2">
                  <div class="cm-field">
                    <textarea
                      v-model="editText"
                      class="cm-textarea"
                      rows="1"
                      maxlength="2000"
                      @input="fitTextarea($event.target)"
                    ></textarea>
                  </div>
                  <div class="d-flex mt-1">
                    <v-btn
                      small
                      color="primary"
                      depressed
                      class="mr-2 elevate-on-hover"
                      :loading="savingEdit"
                      @click="saveEdit(c)"
                    >
                      <v-icon left small>mdi-check</v-icon>{{ $t('Save') }}
                    </v-btn>
                    <v-btn small outlined @click="cancelEdit">
                      <v-icon left small>mdi-cancel</v-icon>{{ $t('Cancel') }}
                    </v-btn>
                  </div>
                </div>
              </v-list-item-content>

              <v-list-item-action class="ml-2" v-if="isCurrentUser(c)">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn icon class="icon-btn" v-bind="attrs" v-on="on" @click="startEdit(c)">
                      <v-icon small>mdi-pencil-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('Edit') }}</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      class="icon-btn danger"
                      v-bind="attrs"
                      v-on="on"
                      :loading="deletingId === c.id"
                      @click="askDelete(c)"
                    >
                      <v-icon small style="color: #c62828">mdi-trash-can</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('Delete') }}</span>
                </v-tooltip>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </transition-group>
      </template>
    </v-card-text>

    <!-- Delete confirm -->
    <v-dialog v-model="deleteDialog" max-width="440">
      <v-card style="border-radius:14px; background:#fff; color:#111; padding:20px; box-shadow:none; border:1px solid #e9eef3;">
        <!-- Title -->
        <div style="display:flex; align-items:center; font-size:17px; font-weight:600; margin-bottom:16px;">
          {{ $t('Delete comment') }}
        </div>

        <!-- Text -->
        <div style="font-size:15px; color:#374151; margin-bottom:22px; line-height:1.4;">
          {{ $t('Do you really want to delete?') }}
        </div>

        <!-- Actions -->
        <div style="display:flex; justify-content:flex-end; gap:10px;">
          <v-btn outlined style="border:1px solid #d1d5db; color:#374151; border-radius:8px; text-transform:none; font-weight:500; min-width:100px; height:40px;" @click="deleteDialog=false">
            {{ $t('Cancel') }}
          </v-btn>
          <v-btn :loading="deleting" style="background:#dc2626; color:#fff; border-radius:8px; text-transform:none; font-weight:600; min-width:100px; height:40px;" @click="confirmDelete">
            {{ $t('Delete') }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Snackbars -->
    <v-snackbar v-model="snack.show" :timeout="2500" top right>
      <v-icon left small>{{ snack.icon }}</v-icon>{{ snack.text }}
    </v-snackbar>
  </v-card>
</template>

<script>
import ToastBus from "~/src/utils/ToastBus";
import { readableTextOn, DARK_TEXT } from "~/src/utils/readable-text";

export default {
  name: "ContractComments",
  props: {
    contractId: { type: [Number, String], required: true },
    apiBase: { type: String, default: "admin/contract2025/comment" },
    allowAllEdits: { type: Boolean, default: true },
    currentUser: {
      type: Object,
      default: () => ({ firstName: "", lastName: "" }),
    },
  },

  data: () => ({
    comments: [],
    loading: false,
    posting: false,
    newComment: "",
    editingId: null,
    editText: "",
    savingEdit: false,
    deleteDialog: false,
    candidateDelete: null,
    deleting: false,
    deletingId: null,
    snack: { show: false, text: "", icon: "mdi-check-circle-outline" },
  }),

  computed: {
    // The "you" chip is light grey in both themes, so its text stays dark (dark theme would paint it white).
    darkText: () => DARK_TEXT,
    newCommentTrimmed() {
      return (this.newComment || "").trim();
    },
  },

  watch: {
    // Clearing the box after posting must shrink it back to one line.
    newComment(val) {
      if (!val) this.$nextTick(() => this.fitTextarea(this.$refs.newCommentEl));
    },
    contractId: {
      immediate: true,
      handler(val) {
        if (val) this.loadComments();
      },
    },
    apiBase() {
      if (this.contractId) this.loadComments();
    },
  },

  methods: {
    // Grow the textarea with its content (up to max-height, then it scrolls).
    fitTextarea(el) {
      if (!el) return;
      el.style.height = "auto";
      el.style.height = el.scrollHeight + 2 + "px";
    },

    // The count chip is primary: navy in the light theme, yellow in the dark one.
    textOn: readableTextOn,

    async loadComments() {
      if (!this.contractId) return;
      this.loading = true;
      try {
        const url = `${this.apiBase}?contractId=${this.contractId}`;
        const data = await this.$axios.$get(url);

        const items = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.data)
          ? data.data
          : [];

        this.comments = items.map(x => ({
          id: x.id,
          contract2025_id: x.contract2025_id,
          comment: x.comment,
          firstName: x.firstName,
          lastName: x.lastName,
          createdAt: x.createdAt || x.created_at || null,
        }));
      } catch (e) {
        console.error("Failed to load comments", e);
        this.comments = [];
        ToastBus.$emit(this.$t("Failed to load comments"))
      } finally {
        this.loading = false;
      }
    },

    initials(fn, ln) {
      const a = (fn || "").trim()[0] || "";
      const b = (ln || "").trim()[0] || "";
      return (a + b).toUpperCase();
    },

    formatDate(iso) {
      try {
        const d = new Date(iso);
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, "0");
        const mi = String(d.getMinutes()).padStart(2, "0");
        return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
      } catch {
        return "";
      }
    },

    isCurrentUser(c) {
      const fn = (this.currentUser.firstName || "").trim().toLowerCase();
      const ln = (this.currentUser.lastName || "").trim().toLowerCase();
      return (
        (c.firstName || "").trim().toLowerCase() === fn &&
        (c.lastName || "").trim().toLowerCase() === ln
      );
    },

    canEditOrDelete(c) {
      return this.allowAllEdits || this.isCurrentUser(c);
    },
     //v-if="isCurrentUser(c)"

    async createComment() {
      const comment = this.newCommentTrimmed;
      if (!comment || this.posting) return;
      this.posting = true;

      // optimistic
      const temp = {
        __tempId: `t-${Date.now()}`,
        firstName: this.currentUser.firstName,
        lastName: this.currentUser.lastName,
        comment,
        createdAt: new Date().toISOString(),
      };
      this.comments.unshift(temp);

      try {
        const dto = {
          contract2025_id: this.contractId,
          comment,
          firstName: this.currentUser.firstName,
          lastName: this.currentUser.lastName,
        };
        const saved = await this.$axios.$post(this.apiBase, dto);

        const idx = this.comments.findIndex((x) => x.__tempId === temp.__tempId);
        if (idx !== -1) this.$set(this.comments, idx, saved);

        this.newComment = "";
        this.$emit("changed", { type: "create", comment: saved });
        ToastBus.$emit("show", this.$t("Comment posted"));
      } catch (e) {
        console.error("Comment submission failed", e);
        this.comments = this.comments.filter((x) => x !== temp);
        ToastBus.$emit(this.$t("Failed to post"))
      } finally {
        this.posting = false;
      }
    },

    startEdit(c) {
      if (!this.canEditOrDelete(c)) return;
      this.editingId = c.id;
      this.editText = c.comment;
    },
    cancelEdit() {
      this.editingId = null;
      this.editText = "";
    },

    async saveEdit(c) {
      if (!this.canEditOrDelete(c)) return;
      const text = (this.editText || "").trim();
      if (!text) return;

      this.savingEdit = true;
      try {
        const dto = {
          contract2025_id: this.contractId,
          comment: text,
          firstName: c.firstName,
          lastName: c.lastName,
        };
        const updated = await this.$axios.$put(`${this.apiBase}/${c.id}`, dto);

        const idx = this.comments.findIndex((x) => x.id === c.id);
        if (idx !== -1) this.$set(this.comments, idx, updated);

        this.$emit("changed", { type: "update", comment: updated });
        this.cancelEdit();
        ToastBus.$emit(this.$t("Comment updated"));
      } catch (e) {
        console.error("Update failed", e);
        ToastBus.$emit(this.$t("Failed to update"));
      } finally {
        this.savingEdit = false;
      }
    },

    askDelete(c) {
      if (!this.canEditOrDelete(c)) return;
      this.candidateDelete = c;
      this.deletingId = c.id;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      if (!this.candidateDelete?.id) {
        this.deleteDialog = false;
        return;
      }
      this.deleting = true;
      try {
        await this.$axios.$delete(`${this.apiBase}/${this.candidateDelete.id}`);
        this.comments = this.comments.filter((x) => x.id !== this.candidateDelete.id);
        this.$emit("changed", { type: "delete", id: this.candidateDelete.id });
        ToastBus.$emit(this.$t("Comment deleted"))
      } catch (e) {
        console.error("Delete failed", e);
        ToastBus.$emit(this.$t("Failed to delete"), "mdi-alert-circle-outline");
      } finally {
        this.deleting = false;
        this.deletingId = null;
        this.candidateDelete = null;
        this.deleteDialog = false;
      }
    },

    toast(text, icon = "mdi-information-outline") {
      this.snack = { show: true, text, icon };
    },
  },
};
</script>

<style scoped>
/* Tokens live on the component root: under `scoped`, `:root` compiles to `[data-v]:root`, which
   matches nothing, so they were undefined in the light theme. */
.comments-card {
  --card-bg: #ffffff;
  --card-border: #e9eef3;
  --divider: #eef2f6;
  --input-border: #94a3b8;
  --input-border-hover: #64748b;
  --item-border: #edf1f5;
  --navy: #00274c;
  --gold: #ffcb05;
}

.comments-card.theme--dark {
  --card-bg: #0f1420;
  --card-border: #1e2a3a;
  --divider: #1f2b3b;
  --input-border: #4b5d7d;
  --input-border-hover: #7186aa;
  --item-border: #1c2736;
}

/* Card + Header — flat, outlined (no shadow) */
.comments-card {
  border-radius: 14px;
  overflow: hidden;
  background: var(--card-bg);
  border: none !important;
  box-shadow: none !important;
  font-size: 15px;
}
.header .text-h6 {
  font-size: 1.2rem;
  /* Vuetify pins .text-h6 to Roboto with !important, so the brand font needs it too. */
  font-family: "Made", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif !important;
}
.soft-divider {
  border-color: var(--divider) !important;
}

/* Inputs */
/* Comment textarea: a plain <textarea>, so scoped CSS reaches it directly. */
/* Input and the Yuborish button share one row; the button stays on the right, level with the input's bottom edge. */
.cm-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}
.cm-row .cm-field {
  flex: 1 1 auto;
  min-width: 0;
}
.cm-row .post-btn {
  flex: 0 0 auto;
  height: 44px;
}
.cm-field {
  position: relative;
}
.cm-textarea {
  display: block;
  width: 100%;
  box-sizing: border-box;
  min-height: 44px;
  max-height: 260px;
  padding: 10px 14px;
  resize: none;
  field-sizing: content; /* auto-grow where supported (also sizes a pre-filled edit box) */
  font: inherit;
  font-family: "Made", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #000;
  caret-color: #000;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 10px;
  outline: 2px solid transparent;
  outline-offset: -1px;
  transition: border-color .15s ease, outline-color .15s ease;
}
.cm-textarea::placeholder {
  color: #94a3b8;
}
/* Hover darkens the border only while the box is not focused; focus hides it (see :focus). */
.cm-textarea:hover:not(:disabled):not(:focus) {
  border-color: var(--input-border-hover);
}
.cm-textarea:focus {
  border-color: transparent; /* focus shows only the offset ring */
  outline: 2px solid var(--navy);
  outline-offset: -1px; /* centred on the (now transparent) 1px border: the ring takes the border's place, no gap */
  box-shadow: none;
}
.cm-textarea:disabled {
  opacity: .6;
  cursor: not-allowed;
}
.theme--dark .cm-textarea {
  color: #e5e7eb; /* black on the dark card would be unreadable */
  caret-color: #e5e7eb;
}
.theme--dark .cm-textarea:focus {
  outline-color: var(--gold);
  box-shadow: none;
}
.cm-count {
  position: absolute;
  right: 12px;
  bottom: 8px;
  font-size: 11px;
  color: #98a2b3;
  pointer-events: none;
}

/* Icon-only buttons — flat, tint on hover */
.icon-btn {
  transition: background-color .2s ease;
  border-radius: 10px !important;
  width: 40px;
  height: 40px;
  box-shadow: none !important;
}
.icon-btn:hover {
  background-color: rgba(0, 39, 76, .06);
}
.icon-btn.danger:hover {
  background-color: rgba(244, 67, 54, .10);
}
.icon-btn .v-icon {
  font-size: 22px;
}

/* List Items — flat, navy border highlight on hover (no shadow) */
.comment-item {
  min-height: 0 !important;
  align-items: flex-start;
  background: var(--card-bg);
  border: none !important;
  box-shadow: none !important;
  transition: background-color .2s ease;
}
.comment-item:hover {
  background-color: rgba(0, 39, 76, .02);
}
.name {
  letter-spacing: .1px;
  display: inline-flex;
  align-items: center;
  font-size: 15px;
}
.comment-body {
  font-size: 15px;
  line-height: 1.4;
  white-space: normal;
  word-break: break-word;
}

/* Chips / Badges */
.you-chip {
  border: none;
  font-size: 13px;
  padding: 0 8px;
}
.count-chip {
  box-shadow: none !important;
  font-size: 14px;
  height: 26px;
}

/* Animations */
.fade-move-enter-active,
.fade-move-leave-active {
  transition: all .22s ease; /* slightly slower for bigger elements */
}
.fade-move-enter,
.fade-move-leave-to {
  opacity: 0;
  transform: translateY(4px);
}
.pulse {
  animation: pulse 1.2s infinite;
}
@keyframes pulse {
  0% { opacity: .5; transform: scale(.95); }
  50% { opacity: 1; transform: scale(1); }
  100% { opacity: .5; transform: scale(.95); }
}

/* Buttons — flat (no shadow) */
.elevate-on-hover {
  transition: background-color .2s ease;
  box-shadow: none !important;
}
.elevate-on-hover:hover {
  box-shadow: none !important;
}
/* Send icon (from 10322482.svg) painted with currentColor, so it always matches the button text. */
.post-icon {
  margin-left: 8px;
  flex: 0 0 auto;
}
.post-btn {
  border-radius: 10px;
  min-height: 42px;
  font-size: 13px !important; /* Vuetify's size rule out-ranks a plain class */
  box-shadow: none !important;
  font-family: "Made", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif !important;
}

/* Avatar — navy, no shadow */
.v-avatar {
  width: 42px !important;
  height: 42px !important;
}
.elev-1 {
  box-shadow: none !important;
}
.avatar-grad {
  background: var(--navy, #00274c) !important;
  color: #fff;
}
.avatar-initials {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: .5px;
}

/* Empty state */
.empty-state {
  font-family: "Made", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif !important; 
  opacity: .8; 
  font-size: 15px;
}

/* Small tweaks for dense list spacing */
.v-list--dense .v-list-item {
  padding-top: 8px;
  padding-bottom: 8px;
}

/* Accessibility: reduce motion */
@media (prefers-reduced-motion: reduce) {
  .fade-move-enter-active,
  .fade-move-leave-active,
  .elevate-on-hover,
  .icon-btn,
  .comment-item { transition: none !important; }
  .pulse { animation: none !important; }
}
</style>
